import type { MediaKind } from "@studio/shared";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Search, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { MediaThumb } from "@/components/MediaThumb";
import { Button, Segmented } from "@/components/ui";
import { api } from "@/lib/api";
import type { LibraryFilter } from "@/router";

const FILTERS: { value: LibraryFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "image", label: "Images" },
  { value: "video", label: "Videos" },
  { value: "audio", label: "Sounds" },
  { value: "document", label: "Documents" },
  { value: "favorites", label: "Favourites" },
];

export function LibraryPage() {
  const { filter, q } = useSearch({ from: "/library" });
  const navigate = useNavigate({ from: "/library" });
  const queryClient = useQueryClient();
  const [text, setText] = useState(q ?? "");
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      if ((q ?? "") !== text) navigate({ search: (prev) => ({ ...prev, q: text || undefined }), replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [text, q, navigate]);

  const kind: MediaKind | undefined = filter === "all" || filter === "favorites" ? undefined : filter;
  const assets = useInfiniteQuery({
    queryKey: ["assets", { kind, q, favorite: filter === "favorites" }],
    queryFn: ({ pageParam }) => api.assets({ kind, q, favorite: filter === "favorites", cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
  });
  const items = assets.data?.pages.flatMap((p) => p.items) ?? [];

  // Load the next page when the end of the grid scrolls into view.
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && assets.hasNextPage && !assets.isFetchingNextPage) void assets.fetchNextPage();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [assets]);

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) await api.upload(file);
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      toast.success(files.length === 1 ? "Added to your library" : `${files.length} files added`);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Library" />
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[1200px] px-4 pb-10 md:px-6">
          <div className="sticky top-0 z-10 -mx-4 flex flex-col gap-3 bg-desk/95 px-4 pb-3 pt-3 backdrop-blur md:-mx-6 md:flex-row md:items-center md:px-6">
            <div className="scrollbar-none -mx-1 overflow-x-auto px-1">
              <Segmented
                value={filter}
                onChange={(value) => navigate({ search: (prev) => ({ ...prev, filter: value }) })}
                options={FILTERS}
              />
            </div>
            <div className="flex flex-1 items-center gap-2">
              <label className="flex h-11 flex-1 items-center gap-2 rounded-[var(--radius-control)] border border-line bg-panel px-3">
                <Search className="size-4 text-mute" aria-hidden />
                <span className="sr-only">Search by prompt or file name</span>
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Search by what you asked for"
                  className="h-full w-full bg-transparent text-[15px] outline-none placeholder:text-mute"
                />
              </label>
              <input
                ref={fileInput}
                type="file"
                multiple
                accept="image/*,video/*,audio/*,application/pdf"
                className="hidden"
                onChange={(e) => upload(e.target.files)}
              />
              <Button variant="outline" onClick={() => fileInput.current?.click()} disabled={uploading}>
                <Upload className="size-4" aria-hidden />
                <span className="hidden sm:inline">{uploading ? "Uploading…" : "Upload"}</span>
              </Button>
            </div>
          </div>

          {items.length === 0 && !assets.isLoading ? (
            <div className="flex min-h-[50dvh] flex-col items-start justify-center gap-3">
              <h2 className="font-display text-[24px]">{q ? "Nothing matches" : "Your library is empty"}</h2>
              <p className="max-w-[52ch] text-mute">
                {q
                  ? "Try other words, or clear the search."
                  : "Everything you create or upload lands here, ready to become the start of something new."}
              </p>
              {!q ? (
                <Link to="/create" search={{ kind: "video" }}>
                  <Button variant="primary">Create something</Button>
                </Link>
              ) : null}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 md:grid-cols-5 md:gap-2 xl:grid-cols-6">
              {items.map((asset) => (
                <Link
                  key={asset.id}
                  to="."
                  search={(prev: Record<string, unknown>) => ({ ...prev, asset: asset.id })}
                  className="rounded-[var(--radius-media)] outline-none focus-visible:ring-2 focus-visible:ring-film"
                >
                  <MediaThumb asset={asset} className="aspect-square transition hover:brightness-110" />
                </Link>
              ))}
            </div>
          )}
          <div ref={sentinel} className="h-10" />
        </div>
      </div>
    </div>
  );
}
